/**
 * TUVI - Cronograma del Proyecto
 * Gantt horizontal tipo Project con scroll y descarga PDF
 * Calcula días hábiles excluyendo fines de semana y festivos de Colombia
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import NombreSemanaEditable from '../NombreSemanaEditable';
import { useNotification } from '../../../context/NotificationContext';
import { useAuth } from '../../../context/AuthContext';
import client from '../../../services/graphqlClient';
import html2pdf from 'html2pdf.js';
import './CronogramaProyecto.css';
import ModalEvidenciasCronograma from './ModalEvidenciasCronograma';
import ModalInformeAvance from './ModalInformeAvance';

const CronogramaProyecto = ({ proyectoId }) => {
  const { success, error } = useNotification();
  const { tienePermiso, user } = useAuth();
  const puedeEditar = tienePermiso('proyectos', 'edicion-cronograma');
  const [cronograma, setCronograma] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editandoFechaInicio, setEditandoFechaInicio] = useState(false);
  const [nuevaFechaInicio, setNuevaFechaInicio] = useState('');
  const [editandoActividad, setEditandoActividad] = useState(null);
  const [editandoDuracion, setEditandoDuracion] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [fechaRealInput, setFechaRealInput] = useState({ id: null, fecha: '' });

  // Estados para modal de nueva actividad
  const [mostrarModalNueva, setMostrarModalNueva] = useState(false);
  const [posicionInsertar, setPosicionInsertar] = useState(null);
  const [nuevaActividad, setNuevaActividad] = useState({ nombre: '', duracion_dias: 1 });
  const [evidenciaActividad, setEvidenciaActividad] = useState(null); // actividad para el modal de evidencias
  const [mostrarInformeAvance, setMostrarInformeAvance] = useState(false);
  // Modal para crear cronograma cuando el proyecto NO lo tiene
  const [mostrarModalCrearCron, setMostrarModalCrearCron] = useState(false);
  const [formCrearCron, setFormCrearCron] = useState({ plazo: 60, fecha_inicio: '' });

  const ganttScrollRef = useRef(null);
  const ganttExportRef = useRef(null);
  const todayLineRef = useRef(null);

  useEffect(() => {
    cargarCronograma();
  }, [proyectoId]);

  // Scroll automático a la línea de HOY
  useEffect(() => {
    if (todayLineRef.current && ganttScrollRef.current) {
      setTimeout(() => {
        const container = ganttScrollRef.current;
        const todayLine = todayLineRef.current;
        const containerRect = container.getBoundingClientRect();
        const todayRect = todayLine.getBoundingClientRect();
        const scrollLeft = todayRect.left - containerRect.left + container.scrollLeft - containerRect.width / 2;
        container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
      }, 300);
    }
  }, [cronograma]);

  const cargarCronograma = async () => {
    try {
      setLoading(true);
      const data = await client.query(`
        query($proyecto_id: Int!) {
          cronogramaProyecto(proyecto_id: $proyecto_id) {
            cronograma {
              id proyecto_id orden nombre etiqueta_semana duracion_dias
              fecha_inicio fecha_fin fecha_inicio_real fecha_fin_real
              estado estado_calculado progreso es_hito es_actual notas
              tareas {
                id orden descripcion tipo estado progreso fecha_completado
                usuario_id usuario_nombre notas
              }
            }
            estadisticas {
              nombre_proyecto apartamento plazo_ejecucion fecha_inicio fecha_fin
              dias_totales dias_transcurridos dias_restantes progreso_porcentaje
              actividades_completadas actividades_en_progreso actividades_pendientes total_actividades
            }
          }
        }
      `, { proyecto_id: proyectoId });

      setCronograma(data.cronogramaProyecto.cronograma || []);
      setEstadisticas(data.cronogramaProyecto.estadisticas);
      if (data.cronogramaProyecto.estadisticas?.fecha_inicio) {
        setNuevaFechaInicio(data.cronogramaProyecto.estadisticas.fecha_inicio);
      }
    } catch (err) {
      console.error('Error cargando cronograma:', err);
      error('Error al cargar el cronograma');
    } finally {
      setLoading(false);
    }
  };

  const handleActualizarFechaInicio = async () => {
    if (!nuevaFechaInicio) return;

    // Detectar si el cronograma actual tiene subtareas.
    // - Sin subtareas (proyecto viejo) → regenerar con plantilla nueva (incluye subtareas).
    // - Con subtareas (estructura nueva) → solo recalcular fechas para no perder marcas.
    const tieneSubtareas = cronograma.some(i => (i.tareas || []).length > 0);
    const plazo = estadisticas?.plazo_ejecucion || 60;

    setGuardando(true);
    try {
      if (!tieneSubtareas) {
        // Regenerar: borra cronograma actual y lo recrea con plantilla + subtareas + nueva fecha.
        // Confirmación porque es destructivo (aunque proyectos sin subtareas suelen estar al inicio).
        const ok = window.confirm(
          `Este proyecto aún no tiene subtareas. Al cambiar la fecha de inicio se regenerará con la plantilla actual de ${plazo} días (incluyendo todas las subtareas). ¿Continuar?`
        );
        if (!ok) {
          setGuardando(false);
          return;
        }
        const data = await client.mutate(
          `mutation($id: Int!, $fecha: String) { regenerarCronograma(proyecto_id: $id, fecha_inicio: $fecha) { success message } }`,
          { id: proyectoId, fecha: nuevaFechaInicio }
        );
        if (data.regenerarCronograma.success) {
          success(data.regenerarCronograma.message || 'Cronograma generado con subtareas');
          setEditandoFechaInicio(false);
          cargarCronograma();
        } else {
          error(data.regenerarCronograma.message || 'Error al regenerar');
        }
      } else {
        // Recalcular solo fechas (manteniendo subtareas y marcas)
        const data = await client.mutate(`
          mutation($proyecto_id: Int!, $fecha_inicio: String) {
            recalcularCronograma(proyecto_id: $proyecto_id, fecha_inicio: $fecha_inicio) { success message }
          }
        `, { proyecto_id: proyectoId, fecha_inicio: nuevaFechaInicio });

        if (data.recalcularCronograma.success) {
          success('Cronograma actualizado');
          setEditandoFechaInicio(false);
          cargarCronograma();
        } else {
          error(data.recalcularCronograma.message || 'Error al actualizar');
        }
      }
    } catch (err) {
      error('Error al actualizar el cronograma');
    } finally {
      setGuardando(false);
    }
  };

  const handleActualizarActividad = async (actividadId, nuevaFecha) => {
    setGuardando(true);
    try {
      const data = await client.mutate(`
        mutation($proyecto_id: Int!, $actividad_id: Int, $actividad_fecha: String) {
          recalcularCronograma(proyecto_id: $proyecto_id, actividad_id: $actividad_id, actividad_fecha: $actividad_fecha) { success message }
        }
      `, { proyecto_id: proyectoId, actividad_id: actividadId, actividad_fecha: nuevaFecha });

      if (data.recalcularCronograma.success) {
        success('Fechas actualizadas');
        setEditandoActividad(null);
        cargarCronograma();
      } else {
        error(data.recalcularCronograma.message || 'Error al actualizar');
      }
    } catch (err) {
      error('Error al actualizar');
    } finally {
      setGuardando(false);
    }
  };

  const handleActualizarDuracion = async (actividadId, nuevaDuracion) => {
    if (!nuevaDuracion || nuevaDuracion < 0) return;

    setGuardando(true);
    try {
      const data = await client.mutate(`
        mutation($proyecto_id: Int!, $actividad_id: Int, $nueva_duracion: Int) {
          recalcularCronograma(proyecto_id: $proyecto_id, actividad_id: $actividad_id, nueva_duracion: $nueva_duracion) { success message }
        }
      `, { proyecto_id: proyectoId, actividad_id: actividadId, nueva_duracion: parseInt(nuevaDuracion) });

      if (data.recalcularCronograma.success) {
        success('Duracion actualizada y fechas recalculadas');
        setEditandoActividad(null);
        setEditandoDuracion({});
        cargarCronograma();
      } else {
        error(data.recalcularCronograma.message || 'Error al actualizar duracion');
      }
    } catch (err) {
      error('Error al actualizar duracion');
    } finally {
      setGuardando(false);
    }
  };

  const handleToggleCompletado = async (item, nuevoEstado) => {
    if (!puedeEditar) return;
    setGuardando(true);
    try {
      const data = await client.mutate(`
        mutation($proyecto_id: Int!, $actividad_id: Int!, $accion: String!) {
          actualizarCronogramaActividad(proyecto_id: $proyecto_id, actividad_id: $actividad_id, accion: $accion) { success message }
        }
      `, { proyecto_id: proyectoId, actividad_id: item.id, accion: nuevoEstado ? 'completar' : 'descompletar' });

      if (data.actualizarCronogramaActividad.success) {
        success(nuevoEstado ? 'Actividad marcada como completada' : 'Actividad desmarcada');
        cargarCronograma();
      } else {
        error(data.actualizarCronogramaActividad.message || 'Error al actualizar estado');
      }
    } catch (err) {
      error('Error al actualizar estado');
    } finally {
      setGuardando(false);
    }
  };

  const handleIniciarReal = async (actividadId, fechaInicioReal) => {
    if (!puedeEditar || !fechaInicioReal) return;
    setGuardando(true);
    try {
      const data = await client.mutate(`
        mutation($proyecto_id: Int!, $actividad_id: Int!, $accion: String!, $fecha_inicio_real: String) {
          actualizarCronogramaActividad(proyecto_id: $proyecto_id, actividad_id: $actividad_id, accion: $accion, fecha_inicio_real: $fecha_inicio_real) { success message }
        }
      `, { proyecto_id: proyectoId, actividad_id: actividadId, accion: 'iniciar_real', fecha_inicio_real: fechaInicioReal });

      if (data.actualizarCronogramaActividad.success) {
        success('Fecha de inicio real registrada');
        setFechaRealInput({ id: null, fecha: '' });
        cargarCronograma();
      } else {
        error(data.actualizarCronogramaActividad.message || 'Error al registrar fecha real');
      }
    } catch (err) {
      error('Error al registrar fecha real');
    } finally {
      setGuardando(false);
    }
  };

  const handleAbrirModalInsertar = (posicion) => {
    setPosicionInsertar(posicion);
    setNuevaActividad({ nombre: '', duracion_dias: 1 });
    setMostrarModalNueva(true);
  };

  const handleInsertarActividad = async () => {
    if (!nuevaActividad.nombre.trim()) {
      error('El nombre de la actividad es requerido');
      return;
    }

    setGuardando(true);
    try {
      const gqlR = await client.mutate(`mutation($pid: Int!, $nombre: String!, $dias: Int!) { insertarActividadAdicional(proyecto_id: $pid, nombre: $nombre, duracion_dias: $dias) { success message } }`, {
        pid: proyectoId, nombre: nuevaActividad.nombre.trim(), dias: parseInt(nuevaActividad.duracion_dias) || 1
      });
      const data = gqlR.insertarActividadAdicional;

      if (data.success) {
        success('Actividad insertada exitosamente');
        setMostrarModalNueva(false);
        setNuevaActividad({ nombre: '', duracion_dias: 1 });
        cargarCronograma();
      } else {
        error(data.message || 'Error al insertar actividad');
      }
    } catch (err) {
      error('Error al insertar actividad');
    } finally {
      setGuardando(false);
    }
  };

  // ============ HELPERS ============
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-CO', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
  };

  const formatDateCompact = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
  };

  const getEstadoClase = (item) => {
    if (item.es_actual) return 'actual';
    if (item.estado_calculado === 'completado') return 'completado';
    if (item.estado_calculado === 'retrasado') return 'retrasado';
    if (item.estado_calculado === 'en_progreso') return 'en-progreso';
    return 'pendiente';
  };

  const getEstadoLabel = (item) => {
    if (item.es_actual) return 'En Curso';
    if (item.estado_calculado === 'completado') return 'Completado';
    if (item.estado_calculado === 'retrasado') return 'Retrasado';
    if (item.estado_calculado === 'en_progreso') return 'En Progreso';
    return 'Pendiente';
  };

  const hoyStr = new Date().toISOString().split('T')[0];

  // ============ GANTT CALCULATIONS ============
  const ganttData = useMemo(() => {
    if (!cronograma.length) return null;

    // Find global min/max dates
    let minDate = cronograma[0].fecha_inicio;
    let maxDate = cronograma[0].fecha_fin;
    cronograma.forEach(item => {
      if (item.fecha_inicio < minDate) minDate = item.fecha_inicio;
      if (item.fecha_fin > maxDate) maxDate = item.fecha_fin;
    });

    const startDate = new Date(minDate + 'T00:00:00');
    const endDate = new Date(maxDate + 'T00:00:00');

    // Add padding: 3 days before and after
    const paddedStart = new Date(startDate);
    paddedStart.setDate(paddedStart.getDate() - 3);
    const paddedEnd = new Date(endDate);
    paddedEnd.setDate(paddedEnd.getDate() + 7);

    const totalDays = Math.ceil((paddedEnd - paddedStart) / (1000 * 60 * 60 * 24));
    const dayWidth = 32; // pixels per day
    const totalWidth = totalDays * dayWidth;

    // Generate week columns for header
    const weeks = [];
    const months = [];
    let currentDate = new Date(paddedStart);
    let lastMonthKey = '';

    while (currentDate <= paddedEnd) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const dayOfWeek = currentDate.getDay();
      const dayNum = currentDate.getDate();
      const monthKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;

      if (monthKey !== lastMonthKey) {
        months.push({
          label: currentDate.toLocaleDateString('es-CO', { month: 'short', year: 'numeric' }),
          startDay: Math.ceil((currentDate - paddedStart) / (1000 * 60 * 60 * 24))
        });
        lastMonthKey = monthKey;
      }

      weeks.push({
        date: dateStr,
        dayNum,
        dayOfWeek,
        isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
        isToday: dateStr === hoyStr,
        dayLabel: currentDate.toLocaleDateString('es-CO', { weekday: 'narrow' })
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Calculate month spans
    for (let i = 0; i < months.length; i++) {
      const nextStart = i < months.length - 1 ? months[i + 1].startDay : totalDays;
      months[i].span = nextStart - months[i].startDay;
    }

    // Calculate bar positions for each activity
    const bars = cronograma.map(item => {
      const itemStart = new Date(item.fecha_inicio + 'T00:00:00');
      const itemEnd = new Date(item.fecha_fin + 'T00:00:00');
      const offsetDays = Math.ceil((itemStart - paddedStart) / (1000 * 60 * 60 * 24));
      const durationDays = Math.ceil((itemEnd - itemStart) / (1000 * 60 * 60 * 24)) + 1;

      return {
        ...item,
        left: offsetDays * dayWidth,
        width: Math.max(durationDays * dayWidth - 4, dayWidth - 4),
        estado: getEstadoClase(item)
      };
    });

    // Today line position
    const todayDate = new Date(hoyStr + 'T00:00:00');
    const todayOffset = Math.ceil((todayDate - paddedStart) / (1000 * 60 * 60 * 24));
    const todayLeft = todayOffset * dayWidth + dayWidth / 2;
    const todayVisible = todayDate >= paddedStart && todayDate <= paddedEnd;

    return { dayWidth, totalWidth, weeks, months, bars, todayLeft, todayVisible, totalDays, paddedStart: paddedStart.getTime(), paddedEnd: paddedEnd.getTime() };
  }, [cronograma, hoyStr]);

  // ============ GANTT SEMANAL (solo para PDF) ============
  const ganttPdfData = useMemo(() => {
    if (!cronograma.length) return null;

    let minDate = cronograma[0].fecha_inicio;
    let maxDate = cronograma[0].fecha_fin;
    cronograma.forEach(item => {
      if (item.fecha_inicio < minDate) minDate = item.fecha_inicio;
      if (item.fecha_fin > maxDate) maxDate = item.fecha_fin;
    });

    const startDate = new Date(minDate + 'T00:00:00');
    const endDate = new Date(maxDate + 'T00:00:00');

    // Retroceder al lunes anterior
    const paddedStart = new Date(startDate);
    const dayOfWeek = paddedStart.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    paddedStart.setDate(paddedStart.getDate() - daysToMonday);

    // Avanzar al domingo siguiente
    const paddedEnd = new Date(endDate);
    const endDow = paddedEnd.getDay();
    if (endDow !== 0) paddedEnd.setDate(paddedEnd.getDate() + (7 - endDow));
    paddedEnd.setDate(paddedEnd.getDate() + 7); // una semana extra

    // Generar semanas
    const weeks = [];
    const months = [];
    let current = new Date(paddedStart);
    let lastMonthKey = '';
    let weekIndex = 0;

    while (current <= paddedEnd) {
      const weekStart = new Date(current);
      const weekEnd = new Date(current);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const monthKey = `${current.getFullYear()}-${current.getMonth()}`;
      if (monthKey !== lastMonthKey) {
        months.push({
          label: current.toLocaleDateString('es-CO', { month: 'short', year: 'numeric' }),
          startWeek: weekIndex
        });
        lastMonthKey = monthKey;
      }

      const startStr = weekStart.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
      const endStr = weekEnd.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });

      weeks.push({
        label: `${startStr}`,
        labelEnd: endStr,
        startDate: weekStart.toISOString().split('T')[0],
        endDate: weekEnd.toISOString().split('T')[0],
        containsToday: hoyStr >= weekStart.toISOString().split('T')[0] && hoyStr <= weekEnd.toISOString().split('T')[0]
      });

      weekIndex++;
      current.setDate(current.getDate() + 7);
    }

    // Calcular spans de meses
    for (let i = 0; i < months.length; i++) {
      const nextStart = i < months.length - 1 ? months[i + 1].startWeek : weeks.length;
      months[i].span = nextStart - months[i].startWeek;
    }

    const weekWidth = 80; // px por semana
    const totalWidth = weeks.length * weekWidth;

    // Calcular barras en escala semanal
    const bars = cronograma.map(item => {
      const itemStart = new Date(item.fecha_inicio + 'T00:00:00');
      const itemEnd = new Date(item.fecha_fin + 'T00:00:00');
      const offsetDays = (itemStart - paddedStart) / (1000 * 60 * 60 * 24);
      const durationDays = (itemEnd - itemStart) / (1000 * 60 * 60 * 24) + 1;
      const pxPerDay = weekWidth / 7;

      return {
        ...item,
        left: offsetDays * pxPerDay,
        width: Math.max(durationDays * pxPerDay - 2, 12),
        estado: getEstadoClase(item)
      };
    });

    // Línea de hoy
    const todayDate = new Date(hoyStr + 'T00:00:00');
    const todayOffsetDays = (todayDate - paddedStart) / (1000 * 60 * 60 * 24);
    const todayLeft = todayOffsetDays * (weekWidth / 7);
    const todayVisible = todayDate >= paddedStart && todayDate <= paddedEnd;

    return { weekWidth, totalWidth, weeks, months, bars, todayLeft, todayVisible };
  }, [cronograma, hoyStr]);

  // ============ Regenerar cronograma ============
  const handleRegenerarCronograma = useCallback(async () => {
    const plazo = estadisticas?.plazo_ejecucion || 60;
    const tieneCronograma = cronograma && cronograma.length > 0;
    const msg = tieneCronograma
      ? `¿Borrar el cronograma actual y regenerarlo desde la plantilla de ${plazo} días? Se perderán las marcas/avances.`
      : `¿Crear cronograma desde plantilla de ${plazo} días?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await client.mutate(
        `mutation($id: Int!) { regenerarCronograma(proyecto_id: $id) { success message } }`,
        { id: proyectoId }
      );
      if (r.regenerarCronograma.success) {
        success(r.regenerarCronograma.message || 'Cronograma regenerado');
        await cargarCronograma();
      } else {
        error(r.regenerarCronograma.message || 'No se pudo regenerar');
      }
    } catch (err) {
      error('Error al regenerar: ' + (err.message || ''));
    }
  }, [proyectoId, estadisticas, cronograma, success, error]);

  // ============ Crear cronograma (proyecto sin cronograma) ============
  const handleCrearCronograma = useCallback(async () => {
    const plazo = parseInt(formCrearCron.plazo) || 60;
    if (![35, 45, 60].includes(plazo)) {
      error('Plazo inválido. Debe ser 35, 45 o 60.');
      return;
    }
    try {
      const r = await client.mutate(
        `mutation($id: Int!, $fecha: String, $plazo: Int) { regenerarCronograma(proyecto_id: $id, fecha_inicio: $fecha, plazo: $plazo) { success message } }`,
        { id: proyectoId, fecha: formCrearCron.fecha_inicio || null, plazo }
      );
      if (r.regenerarCronograma.success) {
        success(r.regenerarCronograma.message || 'Cronograma creado');
        setMostrarModalCrearCron(false);
        setFormCrearCron({ plazo: 60, fecha_inicio: '' });
        await cargarCronograma();
      } else {
        error(r.regenerarCronograma.message || 'No se pudo crear el cronograma');
      }
    } catch (err) {
      error('Error al crear: ' + (err.message || ''));
    }
  }, [proyectoId, formCrearCron, success, error]);

  // ============ Marcar/desmarcar subtarea (actualizacion optimista, sin recarga) ============
  const handleMarcarTarea = useCallback(async (tareaId, estado) => {
    // 1) Snapshot previo para revertir si falla
    let prevTarea = null;
    const ahoraIso = new Date().toISOString();
    const userNombre = user?.nombre_completo || user?.username || null;

    const hoyIso = ahoraIso.slice(0, 10); // YYYY-MM-DD

    setCronograma(prev => prev.map(item => {
      if (!item.tareas) return item;
      const idx = item.tareas.findIndex(t => t.id === tareaId);
      if (idx === -1) return item;
      prevTarea = {
        itemId: item.id, idx, tarea: item.tareas[idx],
        fecha_inicio_real: item.fecha_inicio_real,
        fecha_fin_real: item.fecha_fin_real,
        estado: item.estado,
      };
      const nuevasTareas = [...item.tareas];
      nuevasTareas[idx] = {
        ...nuevasTareas[idx],
        estado,
        fecha_completado: estado === 'completado' ? ahoraIso : null,
        usuario_nombre: estado === 'completado' ? userNombre : nuevasTareas[idx].usuario_nombre,
      };

      // Reflejar el auto-llenado que hace el backend: fecha_inicio_real al primer ítem
      // decidido, fecha_fin_real cuando todos están decididos; sin sobrescribir fechas existentes.
      const decididas = nuevasTareas.filter(t => t.estado !== 'pendiente').length;
      const algunaDecidida = decididas > 0;
      const todasDecididas = decididas === nuevasTareas.length;
      const nuevoEstado = todasDecididas ? 'completado' : (algunaDecidida ? 'en_progreso' : 'pendiente');

      return {
        ...item,
        tareas: nuevasTareas,
        fecha_inicio_real: (algunaDecidida && !item.fecha_inicio_real) ? hoyIso : item.fecha_inicio_real,
        fecha_fin_real:    (todasDecididas && !item.fecha_fin_real)    ? hoyIso : item.fecha_fin_real,
        estado: nuevoEstado,
      };
    }));

    // 2) Disparar la mutation en background
    try {
      const r = await client.mutate(
        `mutation($id: Int!, $estado: String!) { marcarTareaCronograma(id: $id, estado: $estado) { success message } }`,
        { id: tareaId, estado }
      );
      if (!r.marcarTareaCronograma.success) {
        throw new Error(r.marcarTareaCronograma.message || 'No se pudo actualizar la tarea');
      }
    } catch (err) {
      // 3) Revertir si falló (también las fechas/estado del padre)
      if (prevTarea) {
        setCronograma(prev => prev.map(item => {
          if (item.id !== prevTarea.itemId || !item.tareas) return item;
          const nuevasTareas = [...item.tareas];
          nuevasTareas[prevTarea.idx] = prevTarea.tarea;
          return {
            ...item,
            tareas: nuevasTareas,
            fecha_inicio_real: prevTarea.fecha_inicio_real,
            fecha_fin_real: prevTarea.fecha_fin_real,
            estado: prevTarea.estado,
          };
        }));
      }
      error('Error al actualizar tarea: ' + (err.message || ''));
    }
  }, [user, error]);

  // ============ PDF EXPORT (Vista semanal, ocupa toda la hoja) ============
  const handleDescargarPDF = useCallback(async () => {
    if (!ganttPdfData || !cronograma.length) return;
    setGenerandoPDF(true);

    try {
      const PDF_ROW_H = 32;
      const PDF_HEADER_H = 48;
      const PDF_PADDING = 16;
      const PDF_STATS_H = 70; // alto aprox del bloque estadísticas + título
      const nombreProyecto = estadisticas?.nombre_proyecto || 'Proyecto';

      // A3 landscape: 420mm x 297mm, con márgenes de 6mm = 408mm x 285mm
      // A 96dpi => 1 mm ≈ 3.78px, pero html2pdf usa windowWidth directamente
      // Usamos un ancho fijo de página que html2pdf capturará completo
      const PAGE_WIDTH_PX = 1540; // ancho total en px para A3 landscape

      // Calcular sidebar: nombre más largo + padding
      const maxNameLen = cronograma.reduce((max, item) => Math.max(max, item.nombre.length), 0);
      const SIDEBAR_W = Math.min(Math.max(maxNameLen * 7 + 80, 320), 480);

      // Espacio disponible para las semanas = ancho total - sidebar - padding - bordes
      const chartAreaW = PAGE_WIDTH_PX - SIDEBAR_W - (PDF_PADDING * 2) - 4;

      // Recalcular weekWidth dinámico para que las semanas llenen todo el espacio
      const numWeeks = ganttPdfData.weeks.length;
      const weekWidth = Math.max(Math.floor(chartAreaW / numWeeks), 50);
      const totalWidth = numWeeks * weekWidth;

      // Recalcular posiciones de barras y línea de hoy con el nuevo weekWidth
      const { weeks, months } = ganttPdfData;
      const pxPerDay = weekWidth / 7;

      // Recalcular paddedStart para barras
      let minDate = cronograma[0].fecha_inicio;
      cronograma.forEach(item => { if (item.fecha_inicio < minDate) minDate = item.fecha_inicio; });
      const startDate = new Date(minDate + 'T00:00:00');
      const paddedStart = new Date(startDate);
      const dow = paddedStart.getDay();
      const dToMon = dow === 0 ? 6 : dow - 1;
      paddedStart.setDate(paddedStart.getDate() - dToMon);

      const bars = cronograma.map(item => {
        const itemStart = new Date(item.fecha_inicio + 'T00:00:00');
        const itemEnd = new Date(item.fecha_fin + 'T00:00:00');
        const offsetDays = (itemStart - paddedStart) / (1000 * 60 * 60 * 24);
        const durationDays = (itemEnd - itemStart) / (1000 * 60 * 60 * 24) + 1;
        return {
          ...item,
          left: offsetDays * pxPerDay,
          width: Math.max(durationDays * pxPerDay - 2, 14),
          estado: getEstadoClase(item)
        };
      });

      // Recalcular month spans con nuevo weekWidth
      const monthSpans = months.map((m, i) => {
        const nextStart = i < months.length - 1 ? months[i + 1].startWeek : numWeeks;
        return { ...m, span: nextStart - m.startWeek };
      });

      // PDF: todas las barras en gris uniforme para impresión
      const pdfBarStyle = 'background:#9ca3af;color:white;border-radius:4px;';

      // Sidebar rows (sin colores de estado, alternando blanco/gris claro)
      let sidebarRows = '';
      cronograma.forEach((item, i) => {
        const bgColor = i % 2 === 0 ? '#fafbfc' : '#ffffff';
        const nameStyle = item.es_hito ? 'font-weight:700;color:#374151;text-transform:uppercase;font-size:9px;' : 'font-size:10px;color:#374151;';
        sidebarRows += `<div style="display:flex;align-items:center;height:${PDF_ROW_H}px;border-bottom:1px solid #eee;background:${bgColor};padding:0 10px;">
          <span style="width:24px;font-size:9px;color:#9ca3af;font-weight:600;text-align:center;flex-shrink:0;">${i + 1}</span>
          <span style="${nameStyle}flex:1;padding:0 8px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${item.nombre}</span>
          <span style="width:32px;font-size:9px;color:#6b7280;font-weight:600;text-align:center;flex-shrink:0;">${item.duracion_dias}d</span>
        </div>`;
      });

      // Month header cells
      const monthCells = monthSpans.map(m =>
        `<div style="width:${m.span * weekWidth}px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;border-right:1px solid #334155;white-space:nowrap;overflow:hidden;">${m.label}</div>`
      ).join('');

      // Week header cells (sin highlight de hoy)
      const weekCells = weeks.map(w =>
        `<div style="width:${weekWidth}px;display:flex;flex-direction:column;align-items:center;justify-content:center;border-right:1px solid #334155;">
          <span style="font-size:8px;color:#cbd5e1;font-weight:600;line-height:1.3;">${w.label}</span>
          <span style="font-size:7px;color:#64748b;line-height:1.2;">${w.labelEnd}</span>
        </div>`
      ).join('');

      // Grid columns (sin highlight de hoy)
      const gridCols = weeks.map(w =>
        `<div style="width:${weekWidth}px;height:${cronograma.length * PDF_ROW_H}px;border-right:1px solid #f0f0f0;flex-shrink:0;"></div>`
      ).join('');

      // Bar rows (todas las barras en gris uniforme para impresión)
      const barRows = bars.map((bar) => {
        const barStyle = bar.es_hito
          ? `background:#6b7280;color:white;border-radius:3px;min-width:18px;`
          : pdfBarStyle;
        const barLabel = bar.es_hito ? '◆' : (bar.width > 80 ? `${formatDateCompact(bar.fecha_inicio)} — ${formatDateCompact(bar.fecha_fin)}` : bar.width > 40 ? `${bar.duracion_dias}d` : '');

        return `<div style="position:relative;height:${PDF_ROW_H}px;border-bottom:1px solid #f0f0f0;">
          <div style="position:absolute;left:${bar.left}px;width:${bar.width}px;top:${(PDF_ROW_H - 22) / 2}px;height:22px;${barStyle}display:flex;align-items:center;justify-content:center;box-shadow:0 1px 2px rgba(0,0,0,0.08);overflow:hidden;">
            <span style="font-size:8px;font-weight:600;white-space:nowrap;padding:0 4px;">${barLabel}</span>
          </div>
        </div>`;
      }).join('');

      const TOTAL_W = SIDEBAR_W + totalWidth + 4;

      const fullHtml = `
        <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;padding:${PDF_PADDING}px;background:white;width:${TOTAL_W}px;">
          <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:8px;">
            <div>
              <h2 style="margin:0 0 2px;font-size:16px;color:#1e293b;">Cronograma — ${nombreProyecto}</h2>
              <p style="margin:0;font-size:10px;color:#9ca3af;">Generado el ${new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
            <div style="display:flex;gap:24px;align-items:center;">
              <div style="padding:6px 14px;background:#f0fdf4;border-left:3px solid #10b981;border-radius:6px;">
                <span style="font-size:9px;font-weight:700;text-transform:uppercase;color:#6b7280;letter-spacing:0.5px;">Inicio</span>
                <span style="display:block;font-size:14px;font-weight:800;color:#111827;margin-top:1px;">${estadisticas?.fecha_inicio ? formatDate(estadisticas.fecha_inicio) : '-'}</span>
              </div>
              <div style="padding:6px 14px;background:#eff6ff;border-left:3px solid #3b82f6;border-radius:6px;">
                <span style="font-size:9px;font-weight:700;text-transform:uppercase;color:#6b7280;letter-spacing:0.5px;">Entrega</span>
                <span style="display:block;font-size:14px;font-weight:800;color:#111827;margin-top:1px;">${estadisticas?.fecha_fin ? formatDate(estadisticas.fecha_fin) : '-'}</span>
              </div>
            </div>
          </div>
          <div style="display:flex;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;width:${TOTAL_W}px;">
            <div style="width:${SIDEBAR_W}px;min-width:${SIDEBAR_W}px;background:#fafbfc;border-right:2px solid #e5e7eb;">
              <div style="display:flex;align-items:center;height:${PDF_HEADER_H}px;padding:0 10px;background:#1e293b;color:white;font-size:9px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">
                <span style="width:24px;text-align:center;">#</span>
                <span style="flex:1;padding:0 8px;">Actividad</span>
                <span style="width:32px;text-align:center;">Días</span>
              </div>
              ${sidebarRows}
            </div>
            <div style="width:${totalWidth}px;overflow:visible;">
              <div style="width:${totalWidth}px;">
                <div style="display:flex;flex-direction:column;background:#1e293b;height:${PDF_HEADER_H}px;">
                  <div style="display:flex;height:20px;border-bottom:1px solid #334155;">${monthCells}</div>
                  <div style="display:flex;flex:1;">${weekCells}</div>
                </div>
                <div style="position:relative;">
                  <div style="display:flex;position:absolute;top:0;left:0;">${gridCols}</div>
                  ${barRows}
                </div>
              </div>
            </div>
          </div>
          <div style="margin-top:6px;text-align:center;font-size:8px;color:#9ca3af;">TUVI — Sistema de Gestión de Proyectos PARALELO</div>
        </div>`;

      // Crear elemento temporal fuera de pantalla
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-99999px';
      tempDiv.style.top = '0';
      tempDiv.innerHTML = fullHtml;
      document.body.appendChild(tempDiv);

      const contentEl = tempDiv.firstElementChild;

      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Cronograma_${nombreProyecto.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: TOTAL_W + (PDF_PADDING * 2)
        },
        jsPDF: {
          unit: 'mm',
          format: 'a3',
          orientation: 'landscape'
        }
      };

      await html2pdf().set(opt).from(contentEl).save();

      document.body.removeChild(tempDiv);
      success('PDF descargado exitosamente');
    } catch (err) {
      console.error('Error generando PDF:', err);
      error('Error al generar el PDF');
    } finally {
      setGenerandoPDF(false);
    }
  }, [estadisticas, cronograma, ganttPdfData, formatDateCompact, getEstadoClase, success, error]);

  // ============ PDF CLIENTE (Tabla por semanas) ============
  const handleDescargarPDFCliente = useCallback(async () => {
    if (!cronograma.length || !estadisticas) return;
    setGenerandoPDF(true);

    try {
      const nombreProyecto = estadisticas?.nombre_proyecto || 'Proyecto';
      const apartamento = estadisticas?.apartamento || '';
      const fechaInicio = estadisticas?.fecha_inicio;
      const fechaFin = estadisticas?.fecha_fin;
      const plazo = estadisticas?.plazo_ejecucion || 60;

      // Plantillas de etapas según plazo de ejecución
      const PLANTILLAS_CLIENTE = {
        35: [
          { etapa: 'INICIO', fecha: formatDate(fechaInicio), descripcion: null, tipo: 'inicio' },
          { etapa: 'SEMANA 1', fecha: null, descripcion: 'ADMIN (TOMA DE MEDIDAS, COMPRA DE MATERIALES, COORDINACIÓN DE CONTRATISTAS)', tipo: 'normal' },
          { etapa: 'SEMANA 2', fecha: null, descripcion: 'MEDIAS DESPIECES', tipo: 'normal' },
          { etapa: 'SEMANA 3', fecha: null, descripcion: 'CORTE', tipo: 'normal' },
          { etapa: '', fecha: '', descripcion: '3er PAGO DEL CLIENTE', tipo: 'pago' },
          { etapa: 'SEMANA 4', fecha: null, descripcion: 'INICIO INSTALACIÓN', tipo: 'normal' },
          { etapa: 'SEMANA 5', fecha: null, descripcion: 'INSTALACIÓN', tipo: 'normal' },
          { etapa: '', fecha: '', descripcion: '4to PAGO DEL CLIENTE', tipo: 'pago' },
          { etapa: 'SEMANA 6', fecha: null, descripcion: 'MESONES - ELÉCTRICO', tipo: 'normal' },
          { etapa: 'SEMANA 7', fecha: null, descripcion: 'CONTROL DE CALIDAD', tipo: 'normal' },
          { etapa: 'ENTREGA', fecha: formatDate(fechaFin), descripcion: null, tipo: 'entrega' },
          { etapa: '', fecha: '', descripcion: '5to Y ÚLTIMO PAGO DEL CLIENTE', tipo: 'pago' },
        ],
        45: [
          { etapa: 'INICIO', fecha: formatDate(fechaInicio), descripcion: null, tipo: 'inicio' },
          { etapa: 'SEMANA 1', fecha: null, descripcion: 'ADMIN (TOMA DE MEDIDAS, COMPRA DE MATERIALES, COORDINACIÓN DE CONTRATISTAS)', tipo: 'normal' },
          { etapa: 'SEMANA 2', fecha: null, descripcion: 'MORTERO', tipo: 'normal' },
          { etapa: 'SEMANA 3', fecha: null, descripcion: 'ESTUCO', tipo: 'normal' },
          { etapa: 'SEMANA 4', fecha: null, descripcion: 'ESTUCO', tipo: 'normal' },
          { etapa: '', fecha: '', descripcion: '3er PAGO DEL CLIENTE', tipo: 'pago' },
          { etapa: 'SEMANA 5', fecha: null, descripcion: 'ENCHAPE', tipo: 'normal' },
          { etapa: 'SEMANA 6', fecha: null, descripcion: 'ENCHAPE', tipo: 'normal' },
          { etapa: '', fecha: '', descripcion: '4to PAGO DEL CLIENTE', tipo: 'pago' },
          { etapa: 'SEMANA 7', fecha: null, descripcion: 'ACABADO', tipo: 'normal' },
          { etapa: 'SEMANA 8', fecha: null, descripcion: 'ACABADO', tipo: 'normal' },
          { etapa: '', fecha: '', descripcion: '5to PAGO DEL CLIENTE', tipo: 'pago' },
          { etapa: 'SEMANA 9', fecha: null, descripcion: 'CONTROL', tipo: 'normal' },
          { etapa: 'ENTREGA', fecha: formatDate(fechaFin), descripcion: null, tipo: 'entrega' },
        ],
        60: [
          { etapa: 'INICIO', fecha: formatDate(fechaInicio), descripcion: null, tipo: 'inicio' },
          { etapa: 'SEMANA 1', fecha: null, descripcion: 'SEMANA ADMINISTRATIVA (TOMA DE MEDIDAS, COMPRA DE MATERIALES, PAGO DE ANTICIPO DE CARPINTERÍA, COORDINACIÓN DE CONTRATISTAS)', tipo: 'normal' },
          { etapa: 'SEMANA 4', fecha: null, descripcion: 'ESTUCO', tipo: 'normal' },
          { etapa: '', fecha: '', descripcion: '3er PAGO DEL CLIENTE', tipo: 'pago' },
          { etapa: 'SEMANA 6', fecha: null, descripcion: 'ENCHAPE', tipo: 'normal' },
          { etapa: '', fecha: '', descripcion: '4to PAGO DEL CLIENTE', tipo: 'pago' },
          { etapa: 'SEMANA 8', fecha: null, descripcion: 'CARPINTERÍA', tipo: 'normal' },
          { etapa: 'SEMANA 10', fecha: null, descripcion: 'ACABADO EN PINTURA', tipo: 'normal' },
          { etapa: 'SEMANA 12', fecha: null, descripcion: 'CONTROL DE CALIDAD', tipo: 'normal' },
          { etapa: 'ENTREGA', fecha: formatDate(fechaFin), descripcion: null, tipo: 'entrega' },
          { etapa: '', fecha: '', descripcion: '5to Y ÚLTIMO PAGO DEL CLIENTE', tipo: 'pago' },
        ],
      };

      const etapas = PLANTILLAS_CLIENTE[plazo] || PLANTILLAS_CLIENTE[60];

      let tableRows = '';
      etapas.forEach(row => {
        if (row.tipo === 'inicio') {
          tableRows += `
            <tr>
              <td style="padding:14px 16px;font-weight:800;font-size:14px;color:white;border:1px solid #059669;background:#10b981;width:140px;vertical-align:top;">${row.etapa}</td>
              <td style="padding:14px 16px;font-size:16px;font-weight:700;color:#065f46;border:1px solid #d1d5db;background:#ecfdf5;">${row.fecha}</td>
            </tr>`;
        } else if (row.tipo === 'entrega') {
          tableRows += `
            <tr>
              <td style="padding:14px 16px;font-weight:800;font-size:14px;color:white;border:1px solid #1d4ed8;background:#3b82f6;vertical-align:top;">${row.etapa}</td>
              <td style="padding:14px 16px;font-size:16px;font-weight:700;color:#1e3a8a;border:1px solid #d1d5db;background:#eff6ff;">${row.fecha}</td>
            </tr>`;
        } else if (row.tipo === 'pago') {
          tableRows += `
            <tr>
              <td colspan="2" style="padding:8px 16px;font-weight:800;font-size:12px;color:white;background:linear-gradient(90deg,#f59e0b,#f97316);border:1px solid #d97706;text-align:center;letter-spacing:1.5px;text-transform:uppercase;">
                💲 ${row.descripcion}
              </td>
            </tr>`;
        } else {
          tableRows += `
            <tr>
              <td style="padding:14px 16px;font-weight:800;font-size:14px;color:#1e293b;border:1px solid #d1d5db;vertical-align:top;white-space:nowrap;background:#f8fafc;">${row.etapa}</td>
              <td style="padding:14px 16px;border:1px solid #d1d5db;line-height:1.7;">
                <div style="font-size:13px;font-weight:600;color:#1e293b;">${row.descripcion}</div>
              </td>
            </tr>`;
        }
      });

      const fullHtml = `
        <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;padding:32px;background:white;max-width:700px;">
          <div style="text-align:center;margin-bottom:28px;">
            <h1 style="margin:0 0 6px;font-size:22px;color:#1e293b;font-weight:800;letter-spacing:1px;">RUTA DE TU PROYECTO</h1>
            <p style="margin:0 0 4px;font-size:13px;color:#374151;font-weight:500;font-style:italic;">Así avanzaremos paso a paso hasta la entrega de tu apartamento</p>
            <p style="margin:8px 0 0;font-size:15px;color:#1e293b;font-weight:700;">${nombreProyecto}${apartamento ? ' - Apto ' + apartamento : ''}</p>
          </div>

          <table style="width:100%;border-collapse:collapse;border:1px solid #d1d5db;">
            <thead>
              <tr>
                <th style="padding:10px 16px;font-size:11px;font-weight:700;color:white;background:#1e293b;border:1px solid #1e293b;text-align:left;text-transform:uppercase;letter-spacing:0.5px;width:140px;">Etapa</th>
                <th style="padding:10px 16px;font-size:11px;font-weight:700;color:white;background:#1e293b;border:1px solid #1e293b;text-align:left;text-transform:uppercase;letter-spacing:0.5px;">Descripción</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>

          <div style="margin-top:20px;padding-top:16px;border-top:1px solid #e5e7eb;">
            <p style="margin:0;font-size:10px;color:#9ca3af;text-align:center;">
              Este cronograma es una estimación y puede estar sujeto a cambios según el avance de la obra.
            </p>
          </div>
        </div>`;

      // Crear elemento temporal
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-99999px';
      tempDiv.style.top = '0';
      tempDiv.innerHTML = fullHtml;
      document.body.appendChild(tempDiv);

      const contentEl = tempDiv.firstElementChild;

      const opt = {
        margin: [12, 12, 12, 12],
        filename: `Cronograma_Cliente_${nombreProyecto.replace(/[^a-zA-Z0-9]/g, '_')}${apartamento ? '_Apto_' + apartamento.replace(/[^a-zA-Z0-9]/g, '_') : ''}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 760
        },
        jsPDF: {
          unit: 'mm',
          format: 'letter',
          orientation: 'portrait'
        }
      };

      await html2pdf().set(opt).from(contentEl).save();

      document.body.removeChild(tempDiv);
      success('PDF para cliente descargado');
    } catch (err) {
      console.error('Error generando PDF cliente:', err);
      error('Error al generar el PDF');
    } finally {
      setGenerandoPDF(false);
    }
  }, [estadisticas, cronograma, formatDate, formatDateCompact, success, error]);

  // ============ RENDER ============
  if (loading) {
    return (
      <div className="cronograma-loading">
        <div className="spinner"></div>
        <p>Cargando cronograma...</p>
      </div>
    );
  }

  if (cronograma.length === 0) {
    return (
      <>
        <div className="cronograma-empty">
          <div className="empty-icon">📅</div>
          <h3>Sin Cronograma</h3>
          <p>Este proyecto no tiene cronograma configurado</p>
          <button
            className="btn-crear-cronograma"
            onClick={() => {
              setFormCrearCron({ plazo: 60, fecha_inicio: '' });
              setMostrarModalCrearCron(true);
            }}
            style={{
              marginTop: 20,
              padding: '12px 28px',
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            }}
          >
            + Crear cronograma
          </button>
        </div>

        {/* Modal Crear Cronograma */}
        {mostrarModalCrearCron && (
          <div className="modal-overlay" onClick={() => setMostrarModalCrearCron(false)}>
            <div className="modal-insertar-actividad" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
              <div className="modal-header">
                <h3>Crear Cronograma del Proyecto</h3>
                <button className="btn-cerrar" onClick={() => setMostrarModalCrearCron(false)}>×</button>
              </div>
              <div className="modal-body">
                <p className="modal-info" style={{ marginBottom: 20 }}>
                  Selecciona el plazo de ejecución y la fecha de inicio. El cronograma se generará automáticamente con todas sus subtareas.
                </p>
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Plazo de ejecución</label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {[35, 45, 60].map(p => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setFormCrearCron(prev => ({ ...prev, plazo: p }))}
                        style={{
                          flex: 1,
                          padding: '14px 8px',
                          border: formCrearCron.plazo === p ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                          background: formCrearCron.plazo === p ? '#eff6ff' : 'white',
                          borderRadius: 8,
                          cursor: 'pointer',
                          fontSize: '1rem',
                          fontWeight: formCrearCron.plazo === p ? 700 : 500,
                          color: formCrearCron.plazo === p ? '#1d4ed8' : '#475569',
                          transition: 'all 0.15s',
                        }}
                      >
                        {formCrearCron.plazo === p && '✓ '}
                        {p} días
                      </button>
                    ))}
                  </div>
                  <small style={{ display: 'block', marginTop: 8, color: '#64748b' }}>
                    {formCrearCron.plazo === 35 && '7 semanas, 28 subtareas'}
                    {formCrearCron.plazo === 45 && '9 semanas, 33 subtareas'}
                    {formCrearCron.plazo === 60 && '12 semanas, 43 subtareas'}
                  </small>
                </div>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Fecha de inicio (opcional)</label>
                  <input
                    type="date"
                    value={formCrearCron.fecha_inicio}
                    onChange={(e) => setFormCrearCron(prev => ({ ...prev, fecha_inicio: e.target.value }))}
                    style={{ width: '100%', padding: 10, border: '1px solid #cbd5e1', borderRadius: 6 }}
                  />
                  <small style={{ display: 'block', marginTop: 6, color: '#64748b' }}>
                    Si la dejas vacía, se calcula automáticamente al próximo viernes/martes.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-cancelar" onClick={() => setMostrarModalCrearCron(false)}>Cancelar</button>
                <button className="btn-guardar" onClick={handleCrearCronograma}>
                  Crear cronograma de {formCrearCron.plazo} días
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  const ROW_HEIGHT = 52;
  const HEADER_HEIGHT = 54;

  return (
    <div className="cronograma-proyecto">
      {/* Dashboard compacto */}
      {estadisticas && (
        <div className="tl-dashboard">
          <div className="tl-dash-top">
            <div className="tl-dash-progress">
              <div className="tl-progress-ring">
                <svg viewBox="0 0 80 80">
                  <circle className="tl-ring-bg" cx="40" cy="40" r="34" />
                  <circle
                    className="tl-ring-fill"
                    cx="40" cy="40" r="34"
                    style={{ strokeDasharray: `${estadisticas.progreso_porcentaje * 2.136} 213.6` }}
                  />
                </svg>
                <span className="tl-ring-text">{estadisticas.progreso_porcentaje}%</span>
              </div>
              <div className="tl-progress-info">
                <span className="tl-progress-title">Progreso</span>
                <span className="tl-progress-sub">
                  {estadisticas.actividades_completadas || 0}/{estadisticas.total_actividades || cronograma.length} actividades
                </span>
              </div>
            </div>

            <div className="tl-dash-dates">
              <div className="tl-date-chip start">
                <span className="tl-date-chip-label">Inicio</span>
                <span className="tl-date-chip-value">{formatDateShort(estadisticas.fecha_inicio)}</span>
              </div>
              <span className="tl-date-arrow">→</span>
              <div className="tl-date-chip end">
                <span className="tl-date-chip-label">Entrega</span>
                <span className="tl-date-chip-value">{formatDateShort(estadisticas.fecha_fin)}</span>
              </div>
            </div>

            <div className="tl-dash-metrics">
              <div className="tl-metric">
                <span className="tl-metric-num">{estadisticas.dias_totales}</span>
                <span className="tl-metric-lbl">Días totales</span>
              </div>
              <div className="tl-metric">
                <span className={`tl-metric-num ${estadisticas.dias_transcurridos < 0 ? 'blue' : ''}`}>
                  {Math.abs(estadisticas.dias_transcurridos)}
                </span>
                <span className="tl-metric-lbl">
                  {estadisticas.dias_transcurridos < 0 ? 'Para iniciar' : 'Transcurridos'}
                </span>
              </div>
              <div className="tl-metric">
                <span className={`tl-metric-num ${estadisticas.dias_restantes < 0 ? 'red' : ''}`}>
                  {estadisticas.dias_restantes < 0 ? `+${Math.abs(estadisticas.dias_restantes)}` : estadisticas.dias_restantes}
                </span>
                <span className="tl-metric-lbl">
                  {estadisticas.dias_restantes < 0 ? 'Días atraso' : 'Restantes'}
                </span>
              </div>
            </div>
          </div>

          {estadisticas.actividad_actual && (
            <div className="tl-current-activity">
              <span className="tl-current-dot"></span>
              <span className="tl-current-label">En curso:</span>
              <span className="tl-current-name">{estadisticas.actividad_actual.nombre}</span>
            </div>
          )}

          {puedeEditar && (
          <div className="tl-date-control">
            {editandoFechaInicio ? (
              <div className="date-editor">
                <span className="editor-label">Nueva fecha de inicio:</span>
                <input type="date" value={nuevaFechaInicio} onChange={(e) => setNuevaFechaInicio(e.target.value)} />
                <div className="editor-actions">
                  <button className="btn-save" onClick={handleActualizarFechaInicio} disabled={guardando}>
                    {guardando ? 'Guardando...' : 'Aplicar'}
                  </button>
                  <button className="btn-cancel" onClick={() => setEditandoFechaInicio(false)}>Cancelar</button>
                </div>
              </div>
            ) : (
              <button className="btn-change-date" onClick={() => setEditandoFechaInicio(true)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Cambiar fecha de inicio del proyecto
              </button>
            )}
          </div>
          )}
        </div>
      )}

      {/* Botón descargar PDF */}
      <div className="gantt-toolbar">
        <button
          className="gantt-btn-pdf"
          onClick={handleDescargarPDF}
          disabled={generandoPDF}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="12" y1="18" x2="12" y2="12"/>
            <polyline points="9 15 12 18 15 15"/>
          </svg>
          {generandoPDF ? 'Generando PDF...' : 'Descargar PDF'}
        </button>

        <button
          className="gantt-btn-pdf cliente"
          onClick={handleDescargarPDFCliente}
          disabled={generandoPDF}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          {generandoPDF ? 'Generando...' : 'PDF Cliente'}
        </button>

        <button
          className="gantt-btn-pdf"
          style={{ background: '#4a7c59', color: 'white', borderColor: '#3d6349' }}
          onClick={() => setMostrarInformeAvance(true)}
          title="Informe de avance semanal por capítulos (para el cliente)"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="14" y2="17"/>
          </svg>
          Informe Avance
        </button>

        <button
          className="gantt-btn-pdf"
          style={{ background: '#f59e0b', color: 'white', borderColor: '#d97706' }}
          onClick={handleRegenerarCronograma}
          title={`Plazo del proyecto: ${estadisticas?.plazo_ejecucion || 60} días`}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          Regenerar cronograma ({estadisticas?.plazo_ejecucion || 60}d)
        </button>

        {/* Leyenda inline */}
        <div className="gantt-legend">
          <span className="gantt-legend-item"><span className="gantt-legend-dot actual"></span>En Curso</span>
          <span className="gantt-legend-item"><span className="gantt-legend-dot completado"></span>Completado</span>
          <span className="gantt-legend-item"><span className="gantt-legend-dot en-progreso"></span>En Progreso</span>
          <span className="gantt-legend-item"><span className="gantt-legend-dot pendiente"></span>Pendiente</span>
          <span className="gantt-legend-item"><span className="gantt-legend-dot retrasado"></span>Retrasado</span>
          <span className="gantt-legend-item"><span className="gantt-legend-star">★</span>Hito</span>
          <span className="gantt-legend-item"><span className="gantt-legend-today-line"></span>Hoy</span>
        </div>
      </div>

      {/* ===== GANTT CHART ===== */}
      {ganttData && (
        <div className="gantt-wrapper" ref={ganttExportRef}>
          <div className="gantt-container">
            {/* Panel izquierdo fijo: nombres de actividades */}
            <div className="gantt-sidebar">
              {/* Header del sidebar */}
              <div className="gantt-sidebar-header" style={{ height: HEADER_HEIGHT }}>
                <span className="gantt-sidebar-title">#</span>
                <span className="gantt-sidebar-title">Actividad</span>
                <span className="gantt-sidebar-title">Días</span>
              </div>

              {/* Filas de actividades */}
              {cronograma.map((item, index) => {
                const estadoClase = getEstadoClase(item);
                const isCompletado = item.fecha_fin_real != null;
                return (
                  <div
                    key={item.id}
                    className={`gantt-sidebar-row ${estadoClase} gantt-estado-${item.estado_calculado || 'pendiente'} ${item.es_hito ? 'hito' : ''} ${item.es_actual ? 'is-current' : ''}`}
                    style={{ height: ROW_HEIGHT }}
                  >
                    {!item.es_hito && (
                      <input
                        type="checkbox"
                        className="gantt-row-check"
                        checked={isCompletado}
                        disabled={!puedeEditar || guardando}
                        title={isCompletado ? 'Marcar como pendiente' : 'Marcar como completado'}
                        onChange={(e) => handleToggleCompletado(item, e.target.checked)}
                      />
                    )}
                    {item.es_hito && <span className="gantt-row-check-placeholder" />}
                    <span className="gantt-row-num">{index + 1}</span>
                    <span className={`gantt-row-name ${item.es_hito ? 'hito-name' : ''}`} title={item.nombre}>
                      {item.nombre}
                    </span>
                    <span className="gantt-row-days">{item.duracion_dias}d</span>
                    {puedeEditar && (
                    <div className="gantt-row-actions">
                      {!item.es_hito && (
                        <button
                          className="gantt-btn-edit-row"
                          onClick={() => setEditandoActividad(editandoActividad === item.id ? null : item.id)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                      )}
                      <button
                        className="gantt-btn-insert-row"
                        onClick={() => handleAbrirModalInsertar(index + 1)}
                        title="Insertar después"
                      >
                        +
                      </button>
                    </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Panel derecho scrollable: barras Gantt */}
            <div className="gantt-scroll-area" ref={ganttScrollRef}>
              <div className="gantt-chart" style={{ width: ganttData.totalWidth }}>
                {/* Header de tiempo: meses + días */}
                <div className="gantt-time-header" style={{ height: HEADER_HEIGHT }}>
                  {/* Fila de meses */}
                  <div className="gantt-months-row">
                    {ganttData.months.map((month, i) => (
                      <div
                        key={i}
                        className="gantt-month-cell"
                        style={{ width: month.span * ganttData.dayWidth }}
                      >
                        {month.label}
                      </div>
                    ))}
                  </div>
                  {/* Fila de días */}
                  <div className="gantt-days-row">
                    {ganttData.weeks.map((day, i) => (
                      <div
                        key={i}
                        className={`gantt-day-cell ${day.isWeekend ? 'weekend' : ''} ${day.isToday ? 'today' : ''}`}
                        style={{ width: ganttData.dayWidth }}
                      >
                        <span className="gantt-day-num">{day.dayNum}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cuerpo del Gantt: grilla + barras */}
                <div className="gantt-body">
                  {/* Columnas de fondo (días) */}
                  <div className="gantt-grid-cols">
                    {ganttData.weeks.map((day, i) => (
                      <div
                        key={i}
                        className={`gantt-grid-col ${day.isWeekend ? 'weekend' : ''} ${day.isToday ? 'today' : ''}`}
                        style={{
                          width: ganttData.dayWidth,
                          height: cronograma.length * ROW_HEIGHT
                        }}
                      />
                    ))}
                  </div>

                  {/* Línea de HOY */}
                  {ganttData.todayVisible && (
                    <div
                      className="gantt-today-line"
                      ref={todayLineRef}
                      style={{
                        left: ganttData.todayLeft,
                        height: cronograma.length * ROW_HEIGHT + HEADER_HEIGHT
                      }}
                    >
                      <span className="gantt-today-tag">HOY</span>
                    </div>
                  )}

                  {/* Filas con barras */}
                  {ganttData.bars.map((bar, index) => (
                    <div
                      key={bar.id}
                      className="gantt-bar-row"
                      style={{ height: ROW_HEIGHT }}
                    >
                      <div
                        className={`gantt-bar ${bar.estado} gantt-bar-estado-${bar.estado_calculado || 'pendiente'} ${bar.es_hito ? 'hito' : ''}`}
                        style={{
                          left: bar.left,
                          width: bar.width,
                          top: (ROW_HEIGHT - 26) / 2
                        }}
                        title={`${bar.nombre}\n${formatDateCompact(bar.fecha_inicio)} — ${formatDateCompact(bar.fecha_fin)}\n${bar.duracion_dias} dias`}
                      >
                        {bar.es_hito ? (
                          <span className="gantt-bar-hito-icon">★</span>
                        ) : (
                          <span className="gantt-bar-label">
                            {bar.width > 100 ? `${formatDateCompact(bar.fecha_inicio)} — ${formatDateCompact(bar.fecha_fin)}` :
                             bar.width > 50 ? `${bar.duracion_dias}d` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Editor inline removido - fechas reales se gestionan desde Cronograma General */}
        </div>
      )}

      {/* ===== CHECKLIST DE SUBTAREAS POR SEMANA ===== */}
      {cronograma.some(i => (i.tareas || []).length > 0) && (
        <div className="cron-subtareas-section">
          <div className="cron-subtareas-header">
            <h3>Checklist de actividades por semana</h3>
            <p className="cron-subtareas-hint">
              Marca cada subtarea como completada (✓) o no aplica (N/A). La fecha y usuario quedan registrados.
            </p>
          </div>
          <div className="cron-subtareas-grid">
            {cronograma.filter(i => (i.tareas || []).length > 0).map((item, idx) => {
              const total = item.tareas.length;
              const hechas = item.tareas.filter(t => t.estado === 'completado').length;
              const naCount = item.tareas.filter(t => t.estado === 'na').length;
              const pct = total > 0 ? Math.round(((hechas + naCount) / total) * 100) : 0;
              return (
                <div key={item.id} className="cron-semana-card">
                  <div className="cron-semana-card-header">
                    <div>
                      <NombreSemanaEditable item={item} onRenombrada={cargarCronograma} />
                      <div className="cron-semana-meta">
                        {item.fecha_inicio && `📅 ${item.fecha_inicio}`}
                        {item.duracion_dias > 0 && ` · ${item.duracion_dias} días`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                      <button type="button" className="cron-btn-evidencias" title="Evidencias (fotos/documentos)" onClick={() => setEvidenciaActividad(item)}>📎 Evidencias</button>
                      <div className="cron-semana-progress" title={`${hechas} completadas · ${naCount} N/A · ${total} subtareas`}>
                        <div className="cron-semana-progress-bar">
                          <div style={{ width: `${item.progreso ?? pct}%`, background: (item.progreso ?? pct) >= 100 ? '#10b981' : '#3b82f6' }} />
                        </div>
                        <span>{item.progreso ?? pct}%</span>
                      </div>
                    </div>
                  </div>
                  <ul className="cron-tareas-list">
                    {item.tareas.map(t => {
                      const completado = t.estado === 'completado';
                      const esNA = t.estado === 'na';
                      const colorTipo = t.tipo === 'revision_coordinadora' ? '#f59e0b'
                        : t.tipo === 'pago' ? '#10b981' : '#64748b';
                      return (
                        <li key={t.id} className={`cron-tarea-item ${completado ? 'completado' : ''} ${esNA ? 'na' : ''}`}>
                          <div className="cron-tarea-main">
                            <label className="cron-tarea-check">
                              <input
                                type="checkbox"
                                checked={completado}
                                onChange={(e) => handleMarcarTarea(t.id, e.target.checked ? 'completado' : 'pendiente')}
                              />
                              <span className="cron-tarea-desc" style={{ color: colorTipo }}>
                                {t.tipo === 'revision_coordinadora' && '🟤 '}
                                {t.tipo === 'pago' && '💰 '}
                                {t.descripcion}
                              </span>
                            </label>
                            {!esNA && t.tipo !== 'revision_coordinadora' && (
                              <span className="cron-subtarea-pct">
                                <input type="number" min="0" max="100"
                                  key={`sp-${t.id}-${completado ? 100 : (t.progreso ?? 0)}`}
                                  defaultValue={completado ? 100 : (t.progreso ?? 0)}
                                  onClick={(e) => e.stopPropagation()}
                                  onBlur={async (e) => {
                                    const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                                    try {
                                      await client.mutate(`mutation($id: Int!, $p: Int!) { actualizarProgresoSubtarea(id: $id, progreso: $p) { success } }`, { id: t.id, p: v });
                                      cargarCronograma();
                                    } catch {}
                                  }}
                                />%
                              </span>
                            )}
                            <button
                              type="button"
                              className={`cron-btn-na ${esNA ? 'active' : ''}`}
                              onClick={() => handleMarcarTarea(t.id, esNA ? 'pendiente' : 'na')}
                              title={esNA ? 'Quitar N/A' : 'Marcar como No Aplica'}
                            >
                              {esNA ? '✓ N/A' : 'N/A'}
                            </button>
                          </div>
                          {completado && (
                            <div className="cron-tarea-meta">
                              ✓ {t.fecha_completado ? new Date(t.fecha_completado).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                              {t.usuario_nombre && ` · por ${t.usuario_nombre}`}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal para insertar nueva actividad */}
      {mostrarModalNueva && (
        <div className="modal-overlay">
          <div className="modal-insertar-actividad">
            <div className="modal-header">
              <h3>Insertar Nueva Actividad</h3>
              <button className="btn-cerrar" onClick={() => setMostrarModalNueva(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-info">
                La actividad se insertara en la posicion {posicionInsertar + 1} del cronograma.
                Las fechas se recalcularan automaticamente.
              </p>
              <div className="form-group">
                <label>Nombre de la actividad *</label>
                <input
                  type="text"
                  value={nuevaActividad.nombre}
                  onChange={(e) => setNuevaActividad({ ...nuevaActividad, nombre: e.target.value })}
                  placeholder="Ej: Revision de acabados"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Duracion (dias habiles) *</label>
                <input
                  type="number"
                  min="1"
                  value={nuevaActividad.duracion_dias}
                  onChange={(e) => setNuevaActividad({ ...nuevaActividad, duracion_dias: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancelar" onClick={() => setMostrarModalNueva(false)} disabled={guardando}>
                Cancelar
              </button>
              <button
                className="btn-guardar"
                onClick={handleInsertarActividad}
                disabled={guardando || !nuevaActividad.nombre.trim()}
              >
                {guardando ? 'Insertando...' : 'Insertar Actividad'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ModalEvidenciasCronograma
        show={!!evidenciaActividad}
        actividad={evidenciaActividad}
        onClose={() => setEvidenciaActividad(null)}
      />

      <ModalInformeAvance
        show={mostrarInformeAvance}
        proyectoId={proyectoId}
        onClose={() => setMostrarInformeAvance(false)}
      />
    </div>
  );
};

export default CronogramaProyecto;
